import type { en } from "./en";

export const ptBR: typeof en = {
  meta: { title: "Comissão com {artist} | StudioComm", description: "Peça uma comissão para {artist}: escolha as opções, envie referências e veja uma estimativa. Você só paga depois do orçamento." },
  steps: { type: "Tipo", scene: "Cena", characters: "Personagens", contact: "Contato", review: "Termos" },
  header: { commissionWith: "Comissão com", changeArtist: "Trocar artista", browseArtists: "Ver artistas" },
  banner: {
    waitlist: "{artist} está com lista de espera. Você ainda pode enviar, e o orçamento vem quando abrir uma vaga.",
    closed: "{artist} está com a agenda fechada. Você ainda pode enviar, e o pedido será analisado na reabertura.",
  },
  notFound: { title: "Não encontramos esse artista", body: "O link pode estar antigo ou incorreto.", cta: "Ver artistas" },
  loadError: { title: "Algo deu errado ao carregar o artista", retry: "Tentar de novo" },
  nav: { back: "Voltar", next: "Continuar", send: "Enviar pedido", sending: "Enviando…" },
  type: {
    question: "O que vamos criar?",
    hint: "Escolha o tipo de arte. Os preços são pontos de partida, e {artist} confirma no orçamento.",
    countQuestion: "Quantos personagens?",
    countHint: "Cada personagem ganha seu próprio card depois.",
    sameQuestion: "Todos têm o mesmo tipo de corpo?",
    sameYes: "Sim", sameNo: "Não",
    perCharacter: "Personagem {n}",
    from: "a partir de", ask: "Consultar",
    noTypes: "{artist} ainda não listou preços. Descreva o que quer e receba um orçamento.",
  },
  scene: {
    question: "Monte a cena",
    background: "Fundo",
    bg: {
      none: { title: "Nenhum", body: "Transparente ou uma cor lisa." },
      simple: { title: "Simples", body: "{artist} escolhe um fundo simples que combine com a arte, como cores, degradê ou formas suaves. Sem precisar descrever." },
      complex: { title: "Complexo", body: "Um cenário completo que você descreve. As referências de cena vêm no próximo passo." },
    },
    usage: "Para que é?",
    use: { personal: "Pessoal", commercial: "Comercial" },
    usageInfo: "Qual a diferença?",
    usageTip: {
      personal: "Só para você, como foto de perfil, um quadro na parede ou um presente.",
      commercial: "Qualquer coisa que gere dinheiro ou divulgue algo, como streaming, VTubing, produtos, anúncios ou capa de livro.",
    },
    usageHint: "Uso comercial costuma ter taxa, e ela já está na sua estimativa.",
    rating: "Classificação",
    ratings: { sfw: "SFW", suggestive: "Sugestivo", nsfw: "NSFW (18+)" },
    deadline: "Você tem um prazo?",
    noRush: "Sem pressa",
    byDate: "Definir prazo",
    deadlineInfo: "Como funciona o prazo",
    deadlineTip: "{artist} costuma entregar em cerca de {time}.",
    deadlineTipRush: "Se você precisar em menos de 5 dias, uma taxa de urgência entra na sua estimativa.",
    deliveryTime: { one: "1 dia", many: "{days} dias", fallback: "duas semanas" },
    rushNote: "Faltam menos de 5 dias, então a taxa de urgência já está na sua estimativa.",
    addons: "Extras",
  },
  characters: {
    question: "Mostre seus personagens!",
    hint: "Imagens, palavras ou os dois. Quanto mais você compartilhar, menos perguntas {artist} precisa fazer.",
    card: "Personagem {n}",
    name: "Nome (opcional)",
    blocks: {
      look: { title: "Como é o personagem?", placeholder: "Fichas de referência, roupa, cores e detalhes que {artist} precisa acertar, como cicatrizes, cor dos olhos ou acessórios." },
      action: { title: "O que ele está fazendo?", placeholder: "Pose, expressão, clima ou o que você imaginar. Não sabe? Deixe com {artist}." },
      extra: { title: "Mais alguma coisa?", placeholder: "Objetos, uma roupa específica, o que evitar…" },
    },
    dropRefs: "Solte imagens ou toque para enviar",
    scene: "A cena",
    sceneDescription: "Descreva o lugar",
    scenePlaceholder: "ex.: café aconchegante à noite, chuva lá fora",
    notes: "Mais alguma coisa sobre a arte toda?",
    notesPlaceholder: "Opcional: cores, clima, o que evitar…",
  },
  upload: {
    uploading: "Enviando…", retry: "Tentar de novo", remove: "Remover",
    tooBig: "{n} arquivo(s) ignorado(s). Só imagens, até 10MB cada.",
    tooMany: "Só cabem mais {n} imagem(ns) aqui.",
    failed: "Falha no envio",
  },
  contact: {
    question: "Como {artist} fala com você?",
    name: "Seu nome", email: "E-mail", emailHint: "Seu link privado de acompanhamento vai para cá.",
    discord: "Discord (opcional)",
    posting: "{artist} pode postar a arte pronta?",
    post: { yes: "Sim", delayed: "Depois de um tempo", no: "Manter privada" },
    errors: { name: "Diga seu nome", email: "Digite um e-mail válido" },
  },
  review: {
    question: "Revisão rápida e termos",
    edit: "Editar",
    studioTerms: "Termos da StudioComm",
    artistTerms: "Termos extras de {artist}",
    accept: "Li e aceito estes termos",
    termsUpdated: "Os termos acabaram de mudar. Dê uma olhada e aceite de novo.",
    recap: {
      type: "Tipo", characters: "Personagens", background: "Fundo", usage: "Uso", rating: "Classificação",
      deadline: "Prazo", posting: "Postagem", contact: "Contato", notes: "Observações", scene: "Cena", refs: "{n} imagens",
    },
  },
  errors: {
    validation: "Alguns itens precisam de atenção e estão destacados.",
    rateLimited: "Muitas tentativas. Tente de novo em {n}s.",
    network: "Não conseguimos enviar, mas seu pedido está salvo neste dispositivo.",
    retry: "Tentar de novo",
  },
  receipt: {
    title: "Estimativa", total: "Total estimado", from: "a partir de",
    ask: "o artista vai orçar",
    note: "Isto é uma estimativa. {artist} confirma o preço final no orçamento.",
    empty: "Escolha um tipo para ver a estimativa.",
  },
  checklist: {
    title: "O que {artist} precisa",
    progress: "{done} de {total}",
    items: {
      type: "Tipo de corpo de cada personagem", background: "Fundo", usage: "Uso", rating: "Classificação",
      something: "Algo sobre cada personagem", contact: "Contato + postagem", terms: "Termos aceitos",
      actions: "O que cada personagem está fazendo (recomendado)",
    },
  },
  nudges: {
    faceRef: "Tem um close do rosto? Artistas adoram.",
    action: "Conte o que eles estão fazendo. Economiza uma rodada de perguntas.",
    multiRef: "Tem uma ficha de referência ou de roupa? Solte aqui também.",
    complexScene: "Uma foto de um lugar parecido ajuda muito.",
    commercial: "O uso comercial já está no preço, então sem surpresas depois.",
    dismiss: "Entendi",
  },
  success: {
    title: "Pedido enviado!",
    body: "{artist} vai analisar e enviar um orçamento. Você só paga depois de aceitar.",
    next: "Próximos passos",
    steps: ["{artist} envia um orçamento", "Você topa (ou pede ajustes)", "Você paga e a gente começa!"],
    trackingLabel: "Seu link privado de acompanhamento",
    copy: "Copiar link", copied: "Copiado ✓",
    emailed: "Também enviamos por e-mail.",
    another: "Fazer outro pedido", home: "Voltar ao início",
  },
  tracking: {
    title: "Pedido #{ref}",
    loading: "Carregando seu pedido…",
    waiting: "{artist} está analisando seu pedido. Você recebe um e-mail quando o orçamento estiver pronto.",
    timeline: { requested: "Pedido", quoted: "Orçado", accepted: "Aceito", paid: "Pago", in_progress: "Em andamento", delivered: "Entregue" },
    estimateLabel: "Estimativa",
    notFoundTitle: "Este link não funciona mais",
    notFoundBody: "Ele pode ter expirado ou sido substituído por um mais novo. Podemos enviar links novos por e-mail.",
    resendLabel: "Seu e-mail",
    resendCta: "Enviar meus links",
    resendDone: "Se esse e-mail tiver pedidos conosco, links novos estão a caminho.",
  },

  fees: {
    background_simple: "Fundo simples", background_complex: "Fundo complexo",
    extra_character: "Personagem extra · {type}", complex_design: "Design complexo",
    nsfw_content: "NSFW / sugestivo", commercial_use: "Uso comercial", rush_order: "Urgência",
  },
};
